import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// Remova LineChart daqui (lucide)
import {
  DollarSign,
  Filter,
  Sun,
  Moon,
  ArrowUpCircle,
  ArrowDownCircle,
  TrendingUp,
  X,
  ChevronDown,
  Check,
  Layers,
  AlertCircle,
  Receipt,
} from "lucide-react";

import {
  getDadosFluxoCaixa,
  getDadosBacklog2025,
  getDadosBacklog2026,
  getDadosInadimplencia,
  getDadosReceitasFaturadas,
} from "../api";
import SideBarNavDash from "../componets/SideBarNavDash";
import SideBarNavFaturamento from "../componets/SideBarNavFaturamento";

// Adicione LineChart aqui (recharts)
import {
  ResponsiveContainer,
  BarChart,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  ComposedChart,
  Cell,
  ReferenceLine,
} from "recharts";
// ─────────────────────────────────────────────
// Mapeamento Categoria → Contas
// ─────────────────────────────────────────────
const CATEGORIA_MAP = {
  "(+) Receitas (Clientes)": [
    "(+) Receitas Faturadas",
    "(+) Receitas (Backlog 2025)",
    "(+) Receitas (Backlog 2026)",
    "(+) Receitas (Novas vendas 2026)",
    "(+) Receitas Não Operacioanais",
  ],
  "(+) Outras Receitas": ["(+) Outras Entradas"],
  "(+) Distribuição de Lucro YOUX": ["(+) Distribuição de Lucro YOUX"],
  "Despesas Salários": [
    "(-) Saídas Orçamentárias 2025",
    "(-) Salários e Encargos",
    "(-) Empréstimos",
    "(-) Premiação",
  ],
  "Demais Despesas": [
    "(-) Benefícios",
    "(-) Serviços Terceirizados",
    "(-) Marketing e Publicidade",
    "(-) Despesas de Viagens",
    "(-) Administrativas Gerais",
  ],
  "(-) Investimentos": ["(-) Equipamentos e Materiais"],
  "(-) Despesas Financeiras": ["(-) Financeiras"],
  "(-) Impostos": ["(-) Impostos"],
  "(-) Amortização de Empréstimo": ["(-) Empréstimos"],
  "(-) Retirada de Lucro": [
    "(-) Retirada de Lucro",
    "(-) Distribuição de Lucro YOUX",
  ],
  "(-) Outras Saídas": ["(-) Outras Saídas"],
};

// Mapeamento baseado na imagem de classificação
const MAPA_HIERARQUICO = {
  Operacional: {
    "(+) Receitas (Clientes)": [
      "(+) Receitas Faturadas",
      "(+) Receitas (Backlog 2025)",
      "(+) Receitas (Backlog 2026)",
      "(+) Receitas (Novas vendas 2026)",
      "(+) Receitas Não Operacioanais",
    ],
    "(+) Outras Receitas": ["(+) Outras Entradas"],
    "(+) Transferências": ["(+) Distribuição de Lucro YOUX"],
    "Despesas salários": [
      "(-) Saídas Orçamentárias 2025",
      "(-) Salários e Encargos",
      "(-) Benefícios",
      "(-) Premiação",
    ],
    "Demais despesas": [
      "(-) Serviços Terceirizados",
      "(-) Marketing e Publicidade",
      "(-) Despesas de Viagens",
      "(-) Administrativas Gerais",
      "(-) Equipamentos e Materiais",
    ],
    "(-) Impostos": ["(-) Impostos"],
    "(-) Outras Saídas": ["(-) Outras Saídas"],
  },
  Investimento: {
    "(-) Investimentos": ["(-) Investimento"],
  },
  Financiamento: {
    "(-) Despesas Financeiras": ["(-) Financeiras"],
    "(-) Amortização de Empréstimo": ["(-) Empréstimos"],
    "(-) Retirada de Lucro": [
      "(-) Retirada de Lucro",
      "(-) Distribuição de Lucro YOUX",
    ],
  },
};

const getContasFiltradas = (categSelecionadas) => {
  if (!categSelecionadas.length) return null;
  const contas = new Set();
  categSelecionadas.forEach((cat) =>
    (CATEGORIA_MAP[cat] || []).forEach((c) => contas.add(c)),
  );
  return contas;
};

// ─────────────────────────────────────────────
// Mapeamento nome do mês → número (1-12)
// ─────────────────────────────────────────────
const NOME_MES_NUMERO = {
  Janeiro: 1,
  Fevereiro: 2,
  Março: 3,
  Abril: 4,
  Maio: 5,
  Junho: 6,
  Julho: 7,
  Agosto: 8,
  Setembro: 9,
  Outubro: 10,
  Novembro: 11,
  Dezembro: 12,
};

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const parseValue = (val) => {
  if (!val || typeof val !== "string") return 0;
  const clean = val.replace(/\./g, "").replace(",", ".");
  const parsed = parseFloat(clean);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Faz parse de valores no formato "R$ 47.862,90" ou "47.862,90"
 * Usado para os campos de Receitas Faturadas e Inadimplência.
 */
const parseValorBRL = (val) => {
  if (!val) return 0;
  const clean = val
    .toString()
    .replace(/R\$\s*/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  return parseFloat(clean) || 0;
};

const isValorValido = (valor) => {
  if (!valor) return false;
  const numero = parseFloat(valor.replace(/\./g, "").replace(",", "."));
  return numero > 0;
};

const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    v || 0,
  );

const formatPeriodo = (mes, semana) => {
  const numSemana = semana?.match(/\d+/)?.[0] || semana;
  return `${mes}/${numSemana}`;
};

const sortSemanas = (arr) =>
  [...arr].sort((a, b) => {
    const na = parseInt(a?.match(/\d+/)?.[0] || "0");
    const nb = parseInt(b?.match(/\d+/)?.[0] || "0");
    return na - nb;
  });

const ORDEM_MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

// ─────────────────────────────────────────────
// MultiSelect compartilhado
// ─────────────────────────────────────────────
const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder,
  C,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (opt) =>
    onChange(
      value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt],
    );

  const label =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? value[0]
        : `${value.length} selecionados`;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: `1px solid ${C.border}`,
          borderRadius: 7,
          padding: "7px 10px",
          background: C.card,
          color: value.length ? C.text : C.muted,
          fontSize: 12,
          cursor: "pointer",
          gap: 6,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
        <ChevronDown
          size={12}
          color={C.muted}
          style={{
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s",
          }}
        />
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 100,
            background: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
            minWidth: "100%",
            maxWidth: 280,
            maxHeight: 220,
            overflowY: "auto",
          }}
        >
          {options.map((opt) => {
            const sel = value.includes(opt);
            return (
              <div
                key={opt}
                onClick={() => toggle(opt)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontSize: 12,
                  color: sel ? C.brand : C.text,
                  background: sel
                    ? C.card === "#ffffff"
                      ? "#f5f3ff"
                      : "#2d1f5e"
                    : "transparent",
                  fontWeight: sel ? 700 : 400,
                }}
              >
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 4,
                    flexShrink: 0,
                    border: `2px solid ${sel ? C.brand : C.border}`,
                    background: sel ? C.brand : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {sel && <Check size={9} color="#fff" strokeWidth={3} />}
                </div>
                {opt}
              </div>
            );
          })}
          {!options.length && (
            <div style={{ padding: "10px 12px", fontSize: 12, color: C.muted }}>
              Sem opções
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Página Principal
// ─────────────────────────────────────────────
const FluxoCaixaPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLight, setIsLight] = useState(true);

  // ── States de dados ──
  const [dados, setDados] = useState([]);
  const [backlog2025, setBacklog2025] = useState([]);
  const [backlog2026, setBacklog2026] = useState([]);
  const [receitasFaturadas, setReceitasFaturadas] = useState([]);

  const [totalInadimplencia, setTotalInadimplencia] = useState(0);
  const [loading, setLoading] = useState(true);
  const [erroBacklog, setErroBacklog] = useState(false);

  // ── Filtros unificados (Fluxo de Caixa + Backlog) ──
  const [filterMes, setFilterMes] = useState([
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
  ]);
  const [filterSemana, setFilterSemana] = useState([]);
  const [filterCategoria, setFilterCategoria] = useState([]);

  const C = {
    bg: isLight ? "#f8fafc" : "#0f172a",
    card: isLight ? "#ffffff" : "#1e293b",
    border: isLight ? "#e2e8f0" : "#334155",
    text: isLight ? "#1e293b" : "#f1f5f9",
    muted: isLight ? "#64748b" : "#94a3b8",
    brand: "#7C3AED",
    success: "#10b981",
    danger: "#ef4444",
    amber: "#F59E0B",
    cyan: "#06b6d4",
    indigo: "#6366f1",
  };

  // ── LÓGICA DE DATAS PARA LEGENDAS ──
  const hoje = new Date();
  const anoAtual = hoje.getFullYear();
  const indiceMesAtual = hoje.getMonth();

  const getStatusPeriodo = (mes, ano = anoAtual) => {
    if (!mes) return null;
    const indiceMesRow = ORDEM_MESES.indexOf(mes);

    // Anos anteriores já estão realizados
    if (ano < anoAtual) return { label: "Realizado", color: C.success };
    // Anos futuros são projetados
    if (ano > anoAtual) return { label: "Projetado", color: C.indigo };

    // Mesmo ano, verifica o mês
    if (indiceMesRow < indiceMesAtual)
      return { label: "Realizado", color: C.success };
    if (indiceMesRow > indiceMesAtual)
      return { label: "Projetado", color: C.indigo };

    return { label: "Previsto", color: C.amber }; // mês atual
  };

  // ─────────────────────────────────────────────
  // useEffect: busca paralela dos endpoints
  // ─────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    setErroBacklog(false);

    Promise.all([
      getDadosFluxoCaixa(),
      getDadosBacklog2025(),
      getDadosBacklog2026(),
      getDadosInadimplencia(),
      getDadosReceitasFaturadas(),
    ])
      .then(
        ([
          resFluxo,
          resBacklog2025,
          resBacklog2026,
          resInadimplencia,
          resReceitasFaturadas,
        ]) => {
          setDados(resFluxo.data ?? []);
          setReceitasFaturadas(resReceitasFaturadas.data ?? []);
          console.log("res Fluxos :>> ", resFluxo.data ?? []);

          const b2025 = (resBacklog2025.data ?? []).filter((item) =>
            isValorValido(item["Valor Entrega"]),
          );
          const b2026 = (resBacklog2026.data ?? []).filter((item) =>
            isValorValido(item["Valor Entrega"]),
          );

          setBacklog2025(b2025);
          setBacklog2026(b2026);

          // Inadimplência: valor total fixo, sem filtro
          const inadData = resInadimplencia.data ?? [];
          const totalInad = inadData.reduce((acc, r) => {
            const raw = r["Valor Bruto"] ?? "0";
            return acc + parseValorBRL(raw);
          }, 0);
          setTotalInadimplencia(totalInad);
        },
      )
      .catch((err) => {
        console.error("Erro ao buscar dados:", err);
        setErroBacklog(true);
      })
      .finally(() => setLoading(false));
  }, []);

  // ─────────────────────────────────────────────
  // Opções dinâmicas — Fluxo de Caixa
  // ─────────────────────────────────────────────
  const opcoesMes = useMemo(
    () => [...new Set(dados.map((r) => r["Mês"]).filter(Boolean))],
    [dados],
  );
  const opcoesSemana = useMemo(
    () =>
      sortSemanas([...new Set(dados.map((r) => r["Semana"]).filter(Boolean))]),
    [dados],
  );
  const opcoesCategoria = Object.keys(CATEGORIA_MAP);

  // ─────────────────────────────────────────────
  // Stats — Fluxo de Caixa
  // ─────────────────────────────────────────────
  const activeFilters =
    filterMes.length + filterSemana.length + filterCategoria.length;
  const limpar = () => {
    setFilterMes([]);
    setFilterSemana([]);
    setFilterCategoria([]);
  };

  const dadosFiltrados = useMemo(
    () =>
      dados.filter((row) => {
        if (filterMes.length && !filterMes.includes(row["Mês"])) return false;
        if (filterSemana.length && !filterSemana.includes(row["Semana"]))
          return false;
        return true;
      }),
    [dados, filterMes, filterSemana],
  );

  const statusAtualFiltro = useMemo(() => {
    if (dadosFiltrados.length === 0) return null;

    const mesesPresentes = [
      ...new Set(dadosFiltrados.map((r) => r["Mês"]).filter(Boolean)),
    ];
    if (mesesPresentes.length === 0) return null;

    const statusList = mesesPresentes.map((m) => getStatusPeriodo(m));
    const uniqueLabels = [...new Set(statusList.map((s) => s?.label))];

    if (uniqueLabels.length === 1) return statusList[0];
    return { label: "Projetado", color: C.indigo };
  }, [dadosFiltrados, indiceMesAtual]);

  const stats = useMemo(() => {
    if (!dadosFiltrados.length)
      return { receitas: 0, despesas: 0, saldoFinal: 0, variacao: 0 };
    const contasFiltradas = getContasFiltradas(filterCategoria);
    let totalRec = 0,
      totalDesp = 0;
    dadosFiltrados.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (contasFiltradas && !contasFiltradas.has(key)) return;
        const v = parseValue(row[key]);
        if (key.startsWith("(+)")) totalRec += v;
        if (key.startsWith("(-)")) totalDesp += v;
      });
    });
    const ultima = dadosFiltrados[dadosFiltrados.length - 1];
    return {
      receitas: totalRec,
      despesas: totalDesp,
      saldoFinal: parseValue(ultima?.["(=) Resultado"]),
      variacao: totalRec - totalDesp,
    };
  }, [dadosFiltrados, filterCategoria]);

  const dadosCategorias = useMemo(() => {
    const mesesUnicos = [...new Set(dadosFiltrados.map((r) => r["Mês"]))];

    return mesesUnicos
      .map((mes) => {
        const linhasDoMes = dadosFiltrados.filter((r) => r["Mês"] === mes);

        // Função para somar tudo que pertence a uma Classificação (Nível 1)
        const somarNivel1 = (classificacao) => {
          let totalClassificacao = 0;
          const categorias = MAPA_HIERARQUICO[classificacao]; // Nível 2

          Object.values(categorias).forEach((subcategorias) => {
            // Nível 3
            subcategorias.forEach((conta) => {
              linhasDoMes.forEach((row) => {
                if (row[conta]) {
                  totalClassificacao += parseValue(row[conta]);
                }
              });
            });
          });
          return totalClassificacao;
        };

        return {
          mes,
          operacional: somarNivel1("Operacional"),
          investimento: somarNivel1("Investimento"),
          financiamento: somarNivel1("Financiamento"),
        };
      })
      .sort((a, b) => ORDEM_MESES.indexOf(a.mes) - ORDEM_MESES.indexOf(b.mes));
  }, [dadosFiltrados]);

  // ─────────────────────────────────────────────
  // Dados para Gráfico de Cascata (Waterfall DFC)
  // ─────────────────────────────────────────────
  const dadosCascata = useMemo(() => {
    if (!dadosFiltrados.length) return [];

    const ultima = dadosFiltrados[dadosFiltrados.length - 1];
    const primeira = dadosFiltrados[0];

    const saldoInicial = parseValue(primeira?.["Saldo Inicial"]);
    const saldoFinal = parseValue(ultima?.["(=) Resultado"]);

    // Soma líquida de uma classificação: (+) somam, (-) subtraem
    const somarNivel1Net = (classificacao) => {
      let total = 0;
      const categorias = MAPA_HIERARQUICO[classificacao];
      Object.values(categorias).forEach((contas) => {
        contas.forEach((conta) => {
          dadosFiltrados.forEach((row) => {
            if (row[conta]) {
              const v = parseValue(row[conta]);
              if (conta.startsWith("(+)")) total += v;
              else if (conta.startsWith("(-)")) total -= v;
            }
          });
        });
      });
      return total;
    };

    const operacional = somarNivel1Net("Operacional");
    const investimento = somarNivel1Net("Investimento");
    const financiamento = somarNivel1Net("Financiamento");

    // Monta os steps da cascata
    const steps = [
      { name: "Saldo Inicial", value: saldoInicial, isTotal: true },
      { name: "Operacional", value: operacional },
      { name: "Investimento", value: investimento },
      { name: "Financiamento", value: financiamento },
      { name: "Saldo Final", value: saldoFinal, isTotal: true },
    ];

    let cumulative = 0;
    return steps.map((step) => {
      // Barras totais (Saldo Inicial e Saldo Final) partem do zero
      if (step.isTotal) {
        if (step.name === "Saldo Inicial") cumulative = step.value;
        return {
          name: step.name,
          base: 0,
          barValue: step.value,
          rawValue: step.value,
          isPositive: step.value >= 0,
          isTotal: true,
        };
      }
      // Barras intermediárias (delta por classificação)
      const prev = cumulative;
      cumulative += step.value;
      const isPositive = step.value >= 0;
      return {
        name: step.name,
        // Se negativo: empurra a base para o topo do valor negativo
        base: isPositive ? prev : prev + step.value,
        barValue: Math.abs(step.value),
        rawValue: step.value,
        isPositive,
        isTotal: false,
      };
    });
  }, [dadosFiltrados]);

  // ─────────────────────────────────────────────
  // Stats — Backlog (usa filtro unificado de Mês/Semana)
  // ─────────────────────────────────────────────
  const filtrarBacklog = (lista) =>
    lista.filter((row) => {
      if (filterMes.length && !filterMes.includes(row["Mês Entrega"]))
        return false;
      if (filterSemana.length && !filterSemana.includes(row["Semana"]))
        return false;
      return true;
    });

  const b25Filtrado = useMemo(
    () => filtrarBacklog(backlog2025),
    [backlog2025, filterMes, filterSemana],
  );
  const b26Filtrado = useMemo(
    () => filtrarBacklog(backlog2026),
    [backlog2026, filterMes, filterSemana],
  );

  const statsBacklog = useMemo(() => {
    const soma = (lista) =>
      lista.reduce((acc, r) => acc + parseValue(r["Valor Entrega"]), 0);
    const t25 = soma(b25Filtrado);
    const t26 = soma(b26Filtrado);
    return {
      total2025: t25,
      total2026: t26,
      totalGeral: t25 + t26,
      qtd2025: b25Filtrado.length,
      qtd2026: b26Filtrado.length,
    };
  }, [b25Filtrado, b26Filtrado]);

  const statsReceitasFaturadas = useMemo(() => {
    const mesHoje = hoje.getMonth() + 1;
    const mesesNumFiltro = filterMes
      .map((m) => NOME_MES_NUMERO[m])
      .filter(Boolean);

    const filtered = receitasFaturadas.filter((row) => {
      const mesNum = parseInt(row["Mês recebimento"], 10);

      if (mesesNumFiltro.length && !mesesNumFiltro.includes(mesNum))
        return false;

      if (
        filterSemana.length &&
        !filterSemana.includes(row["Semana Recebimento"])
      )
        return false;

      return true;
    });

    let total = 0;
    let qtdRecebido = 0;
    let qtdPrevisto = 0;

    filtered.forEach((row) => {
      const anoRec = parseInt(row["Ano Recebimento"], 10) || anoAtual;
      const mesRec = parseInt(row["Mês recebimento"], 10) || 0;

      const periodoJaPassou =
        anoRec < anoAtual || (anoRec === anoAtual && mesRec < mesHoje);

      if (periodoJaPassou) {
        total += parseValorBRL(row["Valor Recebido"]);
        qtdRecebido++;
      } else {
        total += parseValorBRL(row["Valor Líquido"]);
        qtdPrevisto++;
      }
    });

    return { total, qtd: filtered.length, qtdRecebido, qtdPrevisto };
  }, [receitasFaturadas, filterMes, filterSemana, anoAtual, hoje]);

  const isDashRoute = location.pathname.includes("-dash");
  // ─────────────────────────────────────────────
  // Dados Preparados para o Gráfico (Recharts)
  // ─────────────────────────────────────────────
  const dadosGrafico = useMemo(() => {
    return dadosFiltrados.map((row) => {
      const cf = getContasFiltradas(filterCategoria);
      let entradas = 0;
      let saidas = 0;

      Object.keys(row).forEach((k) => {
        if (cf && !cf.has(k)) return;
        if (k.startsWith("(+)")) entradas += parseValue(row[k]);
        if (k.startsWith("(-)")) saidas += parseValue(row[k]);
      });

      return {
        label: formatPeriodo(row["Mês"], row["Semana"]), // Eixo X: Mês/Semana
        entradas, // Eixo Y: Barras verdes
        saidas, // Eixo Y: Barras vermelhas
        saldo: parseValue(row["(=) Resultado"]), // Eixo Y: Linha roxa (Saldo)
      };
    });
  }, [dadosFiltrados, filterCategoria]);

  // ─────────────────────────────────────────────
  // Dados — Comparativo Faturado vs Backlog 2026
  // Fonte: getDadosFluxoCaixa (dados filtrados)
  //   Faturadas  → "(+) Receitas Faturadas"
  //   Backlog    → "(+) Receitas (Backlog 2025)"
  //              + "(+) Receitas (Backlog 2026)"
  //              + "(+) Receitas (Novas vendas 2026)"
  // ─────────────────────────────────────────────
  const dadosComparativoFaturamento = useMemo(() => {
    const CHAVE_FATURADAS = "(+) Receitas Faturadas";
    const CHAVES_BACKLOG = [
      "(+) Receitas (Backlog 2025)",
      "(+) Receitas (Backlog 2026)",
      "(+) Receitas (Novas vendas 2026)",
    ];

    const faturadoMap = {};
    const backlogMap = {};

    dadosFiltrados.forEach((row) => {
      const mes = row["Mês"];
      if (!mes) return;

      if (!faturadoMap[mes]) faturadoMap[mes] = 0;
      if (!backlogMap[mes]) backlogMap[mes] = 0;

      faturadoMap[mes] += parseValue(row[CHAVE_FATURADAS]);
      CHAVES_BACKLOG.forEach((chave) => {
        backlogMap[mes] += parseValue(row[chave]);
      });
    });

    const todosMeses = [
      ...new Set(dadosFiltrados.map((r) => r["Mês"]).filter(Boolean)),
    ];

    return todosMeses
      .map((mes) => ({
        mes,
        faturado: faturadoMap[mes] ?? 0,
        backlog: backlogMap[mes] ?? 0,
        gap: (backlogMap[mes] ?? 0) - (faturadoMap[mes] ?? 0),
      }))
      .sort((a, b) => ORDEM_MESES.indexOf(a.mes) - ORDEM_MESES.indexOf(b.mes));
  }, [dadosFiltrados]);

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        background: C.bg,
        fontFamily: "'DM Sans', sans-serif",
        color: C.text,
      }}
    >
      {isDashRoute ? (
        <SideBarNavDash isLight={isLight} />
      ) : (
        <SideBarNavFaturamento isLight={isLight} />
      )}

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {/* Header */}
        <header
          style={{
            background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
            color: "#fff",
            padding: "14px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ marginLeft: 12, fontSize: 13, opacity: 0.7 }}>
              / Fluxo de Caixa
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setIsLight(!isLight)} style={btnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />}{" "}
              {isLight ? "Claro" : "Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={btnStyle}>
              Menu Principal
            </button>
          </div>
        </header>

        <main
          style={{
            padding: "24px 28px",
            maxWidth: 1400,
            margin: "0 auto",
            width: "100%",
          }}
        >
          {/* ── Filtro Unificado (Fluxo de Caixa + Backlog) ── */}
          <div
            style={{
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              padding: 16,
              marginBottom: 20,
              boxShadow: isLight
                ? "0 1px 4px rgba(0,0,0,0.06)"
                : "0 1px 4px rgba(0,0,0,0.3)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <Filter size={14} color={C.muted} />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.muted,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Filtros — Fluxo de Caixa &amp; Backlog
              </span>
              {activeFilters > 0 && (
                <span
                  style={{
                    background: C.brand,
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    borderRadius: 20,
                    padding: "1px 7px",
                  }}
                >
                  {activeFilters} ativo(s)
                </span>
              )}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 2fr",
                gap: 12,
                marginBottom: activeFilters > 0 ? 12 : 0,
              }}
            >
              <div>
                <label style={lbl(C)}>Mês</label>
                <MultiSelect
                  options={opcoesMes}
                  placeholder="Todos os meses"
                  value={filterMes}
                  C={C}
                  onChange={setFilterMes}
                />
              </div>
              <div>
                <label style={lbl(C)}>Semana</label>
                <MultiSelect
                  options={opcoesSemana}
                  placeholder="Todas as semanas"
                  value={filterSemana}
                  C={C}
                  onChange={setFilterSemana}
                />
              </div>
              <div>
                <label style={lbl(C)}>Categoria / Conta</label>
                <MultiSelect
                  options={opcoesCategoria}
                  placeholder="Todas as categorias"
                  value={filterCategoria}
                  C={C}
                  onChange={setFilterCategoria}
                />
              </div>
            </div>
            {activeFilters > 0 && (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button onClick={limpar} style={clrBtn(C)}>
                  <X size={12} /> Limpar Filtros
                </button>
              </div>
            )}
          </div>

          {/* ══════════════════════════════════════
              SEÇÃO KPIs + BACKLOG + INADIMPLÊNCIA
          ══════════════════════════════════════ */}
          <div
            style={{
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              marginBottom: 20,
              overflow: "hidden",
              boxShadow: isLight
                ? "0 1px 4px rgba(0,0,0,0.06)"
                : "0 1px 4px rgba(0,0,0,0.3)",
            }}
          >
            {/* ── KPIs: Fluxo de Caixa ── */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4,1fr)",
                gap: 14,
                marginBottom: 20,
                padding: 16,
              }}
            >
              {/* 1 — Receitas Acumuladas */}
              <KpiCard
                icon={ArrowUpCircle}
                label="Receitas Acumuladas"
                value={fmtBRL(stats.receitas)}
                C={C}
                color={C.success}
                status={statusAtualFiltro}
              />

              {/* 2 — Saídas Acumuladas */}
              <KpiCard
                icon={ArrowDownCircle}
                label="Saídas Acumuladas"
                value={fmtBRL(stats.despesas)}
                C={C}
                color={C.danger}
                status={statusAtualFiltro}
              />

              {/* 3 — Resultado Líquido */}
              <KpiCard
                icon={TrendingUp}
                label="Resultado Líquido"
                value={fmtBRL(stats.variacao)}
                C={C}
                color={C.brand}
                status={statusAtualFiltro}
              />

              {/* 4 — Saldo em Conta */}
              <KpiCard
                icon={DollarSign}
                label="Saldo em Conta"
                value={fmtBRL(stats.saldoFinal)}
                C={C}
                color={C.amber}
                sub="Última linha do período"
                status={statusAtualFiltro}
              />

              {/* 5 — Backlog 2025 */}
              <KpiCard
                icon={TrendingUp}
                label="Backlog 2025"
                value={fmtBRL(statsBacklog.total2025)}
                sub={`${statsBacklog.qtd2025} itens`}
                C={C}
                color={C.cyan}
                status={getStatusPeriodo("Dezembro", 2025)}
              />

              {/* 6 — Backlog 2026 */}
              <KpiCard
                icon={TrendingUp}
                label="Backlog 2026"
                value={fmtBRL(statsBacklog.total2026)}
                sub={`${statsBacklog.qtd2026} itens`}
                C={C}
                color={C.indigo}
              />

              {/* 7 — Inadimplência (valor total fixo, sem filtro) */}
              <div
                style={{
                  border: `1px solid ${C.danger}44`,
                  borderRadius: 10,
                  padding: "18px 20px",
                  background: isLight ? "#fff5f5" : "#1f1215",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <AlertCircle size={14} color={C.danger} />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Inadimplência
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.danger,
                      background: `${C.danger}18`,
                      borderRadius: 20,
                      padding: "2px 8px",
                    }}
                  >
                    Total geral
                  </span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>
                  {fmtBRL(totalInadimplencia)}
                </div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                  Valor total · não sofre filtro
                </div>
              </div>

              {/* 8 — Faturamento (Receitas Faturadas) */}
              <div
                style={{
                  border: `1px solid ${C.cyan}44`,
                  borderRadius: 10,
                  padding: "18px 20px",
                  background: isLight ? "#f0fdfa" : "#0d2b2e",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {/* Cabeçalho do card */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <Receipt size={14} color={C.cyan} />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.muted,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Faturamento
                    </span>
                  </div>
                  {statusAtualFiltro && (
                    <BadgeStatus status={statusAtualFiltro} />
                  )}
                </div>

                {/* Valor principal */}
                <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>
                  {fmtBRL(statsReceitasFaturadas.total)}
                </div>

                {/* Barras de composição: recebido vs previsto */}
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  {statsReceitasFaturadas.qtd > 0 ? (
                    <>
                      <MiniBar
                        label="Rec."
                        valor={statsReceitasFaturadas.qtdRecebido}
                        total={statsReceitasFaturadas.qtd}
                        color={C.success}
                        C={C}
                      />
                      <MiniBar
                        label="Prev."
                        valor={statsReceitasFaturadas.qtdPrevisto}
                        total={statsReceitasFaturadas.qtd}
                        color={C.amber}
                        C={C}
                      />
                    </>
                  ) : (
                    <span style={{ fontSize: 10, color: C.muted }}>
                      Sem NFs no período
                    </span>
                  )}
                  <span style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                    {statsReceitasFaturadas.qtd} NF(s) · período filtrado
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* ══════════════════════════════════════
              GRÁFICO: VISÃO PERÍODO (ENTRADAS/SAÍDAS/SALDO)
          ══════════════════════════════════════ */}
          {dadosFiltrados.length > 0 && !loading && (
            <div
              style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                padding: 20,
                marginBottom: 20,
                boxShadow: isLight
                  ? "0 1px 4px rgba(0,0,0,0.06)"
                  : "0 1px 4px rgba(0,0,0,0.3)",
              }}
            >
              <div
                style={{
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: C.muted,
                    textTransform: "uppercase",
                  }}
                >
                  Evolução do Fluxo de Caixa
                </span>
              </div>

              <div style={{ width: "100%", height: 350 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={dadosGrafico}
                    margin={{ top: 10, right: 10, bottom: 0, left: 10 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={C.border}
                      vertical={false}
                    />

                    <XAxis
                      dataKey="label"
                      stroke={C.muted}
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      dy={10}
                    />

                    <YAxis
                      stroke={C.muted}
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      dx={-10}
                      tickFormatter={(val) =>
                        val >= 1000
                          ? `R$ ${(val / 1000000).toFixed(0)}M`
                          : `R$ ${val}`
                      }
                    />

                    <Tooltip
                      contentStyle={{
                        backgroundColor: C.card,
                        borderColor: C.border,
                        borderRadius: 8,
                        color: C.text,
                      }}
                      formatter={(value) => [fmtBRL(value)]}
                      cursor={{
                        fill: isLight
                          ? "rgba(0,0,0,0.04)"
                          : "rgba(255,255,255,0.04)",
                      }}
                    />

                    <Legend wrapperStyle={{ fontSize: 12, paddingTop: 20 }} />

                    <Bar
                      dataKey="entradas"
                      name="Entradas"
                      fill={C.success}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={40}
                    />
                    <Bar
                      dataKey="saidas"
                      name="Saídas"
                      fill={C.danger}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={40}
                    />
                    <Line
                      type="monotone"
                      dataKey="saldo"
                      name="Saldo Atual"
                      stroke={C.brand}
                      strokeWidth={3}
                      dot={{ r: 4, strokeWidth: 2 }}
                      activeDot={{ r: 6 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {/* ══════════════════════════════════════
              GRÁFICOS: CLASSIFICAÇÃO + CASCATA (lado a lado)
          ══════════════════════════════════════ */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              marginBottom: 20,
            }}
          >
            {/* ── Evolução por Classificação ── */}
            <div
              style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                padding: 20,
                boxShadow: isLight
                  ? "0 1px 4px rgba(0,0,0,0.06)"
                  : "0 1px 4px rgba(0,0,0,0.3)",
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: C.muted,
                  textTransform: "uppercase",
                }}
              >
                Evolução por Classificação
              </span>
              <div style={{ width: "100%", height: 350, marginTop: 20 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dadosCategorias}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={C.border}
                      vertical={false}
                    />
                    <XAxis dataKey="mes" stroke={C.muted} fontSize={12} />
                    <YAxis
                      stroke={C.muted}
                      fontSize={12}
                      tickFormatter={(v) =>
                        v >= 1000000
                          ? `${(v / 1000000).toFixed(1)}M`
                          : fmtBRL(v)
                      }
                    />
                    <Tooltip formatter={(v) => fmtBRL(v)} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="operacional"
                      name="Operacional"
                      stroke={C.danger}
                      strokeWidth={3}
                      dot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="investimento"
                      name="Investimento"
                      stroke={C.success}
                      strokeWidth={3}
                      dot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="financiamento"
                      name="Financiamento"
                      stroke={C.cyan}
                      strokeWidth={3}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ── Gráfico de Cascata (Waterfall DFC) ── */}
            <div
              style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                padding: 20,
                boxShadow: isLight
                  ? "0 1px 4px rgba(0,0,0,0.06)"
                  : "0 1px 4px rgba(0,0,0,0.3)",
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: C.muted,
                  textTransform: "uppercase",
                }}
              >
                Composição do Saldo (Cascata)
              </span>
              <div style={{ width: "100%", height: 350, marginTop: 20 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={dadosCascata}
                    margin={{ top: 10, right: 10, bottom: 0, left: 10 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={C.border}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      stroke={C.muted}
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      dy={10}
                    />
                    <YAxis
                      stroke={C.muted}
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      dx={-6}
                      tickFormatter={(v) =>
                        Math.abs(v) >= 1000000
                          ? `${(v / 1000000).toFixed(1)}M`
                          : `${(v / 1000).toFixed(0)}k`
                      }
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: C.card,
                        borderColor: C.border,
                        borderRadius: 8,
                        color: C.text,
                        fontSize: 12,
                      }}
                      cursor={{
                        fill: isLight
                          ? "rgba(0,0,0,0.04)"
                          : "rgba(255,255,255,0.04)",
                      }}
                      formatter={(value, name, props) => {
                        if (name === "base") return null;
                        const raw = props.payload?.rawValue;
                        return [fmtBRL(raw ?? value), props.payload?.name];
                      }}
                      itemStyle={{ color: C.text }}
                    />
                    {/* Barra invisível de base (offset) */}
                    <Bar
                      dataKey="base"
                      stackId="waterfall"
                      fill="transparent"
                      legendType="none"
                    />
                    {/* Barra visível do delta */}
                    <Bar
                      dataKey="barValue"
                      name="Valor"
                      stackId="waterfall"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={52}
                    >
                      {dadosCascata.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            entry.isTotal
                              ? C.brand
                              : entry.isPositive
                                ? C.success
                                : C.danger
                          }
                          fillOpacity={entry.isTotal ? 1 : 0.85}
                        />
                      ))}
                    </Bar>
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              {/* Legenda manual */}
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  justifyContent: "center",
                  marginTop: 8,
                  flexWrap: "wrap",
                }}
              >
                {[
                  { color: C.brand, label: "Saldo" },
                  { color: C.success, label: "Positivo" },
                  { color: C.danger, label: "Negativo" },
                ].map(({ color, label }) => (
                  <div
                    key={label}
                    style={{ display: "flex", alignItems: "center", gap: 5 }}
                  >
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: color,
                      }}
                    />
                    <span style={{ fontSize: 11, color: C.muted }}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* ══════════════════════════════════════
              GRÁFICO: FATURADO vs BACKLOG 2026
          ══════════════════════════════════════ */}
          {dadosComparativoFaturamento.length > 0 && !loading && (
            <div
              style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                padding: 20,
                marginBottom: 20,
                boxShadow: isLight
                  ? "0 1px 4px rgba(0,0,0,0.06)"
                  : "0 1px 4px rgba(0,0,0,0.3)",
              }}
            >
              {/* Cabeçalho do gráfico */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  marginBottom: 20,
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <div>
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: C.muted,
                      textTransform: "uppercase",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Faturado vs Backlog 2026 — por Período
                  </span>
                  <span style={{ fontSize: 11, color: C.muted }}>
                    Receitas Faturadas × Backlog (2025 + 2026 + Novas Vendas) —
                    fonte: Fluxo de Caixa
                  </span>
                </div>
                {/* Totalizadores rápidos */}
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  {[
                    {
                      label: "Total Faturadas",
                      value: dadosComparativoFaturamento.reduce(
                        (s, r) => s + r.faturado,
                        0,
                      ),
                      color: C.success,
                    },
                    {
                      label: "Total Backlog",
                      value: dadosComparativoFaturamento.reduce(
                        (s, r) => s + r.backlog,
                        0,
                      ),
                      color: C.indigo,
                    },
                  ].map(({ label, value, color }) => (
                    <div
                      key={label}
                      style={{
                        background: `${color}14`,
                        border: `1px solid ${color}33`,
                        borderRadius: 8,
                        padding: "6px 14px",
                        textAlign: "right",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          color: C.muted,
                          marginBottom: 2,
                        }}
                      >
                        {label}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 800, color }}>
                        {fmtBRL(value)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ width: "100%", height: 360 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dadosComparativoFaturamento}
                    margin={{ top: 10, right: 16, bottom: 0, left: 16 }}
                    barCategoryGap="28%"
                    barGap={4}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={C.border}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="mes"
                      stroke={C.muted}
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      dy={10}
                    />
                    <YAxis
                      stroke={C.muted}
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                      dx={-6}
                      tickFormatter={(v) =>
                        v >= 1000000
                          ? `${(v / 1000000).toFixed(1)}M`
                          : `${(v / 1000).toFixed(0)}k`
                      }
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: C.card,
                        borderColor: C.border,
                        borderRadius: 8,
                        color: C.text,
                        fontSize: 12,
                      }}
                      cursor={{
                        fill: isLight
                          ? "rgba(0,0,0,0.04)"
                          : "rgba(255,255,255,0.04)",
                      }}
                      formatter={(value, name) => [fmtBRL(value), name]}
                      labelFormatter={(label) => (
                        <span style={{ fontWeight: 700, color: C.text }}>
                          {label}
                        </span>
                      )}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 12, paddingTop: 20 }}
                      formatter={(value) => (
                        <span style={{ color: C.text }}>{value}</span>
                      )}
                    />
                    <ReferenceLine y={0} stroke={C.border} />

                    {/* Barra: Receitas Faturadas */}
                    <Bar
                      dataKey="faturado"
                      name="Faturadas"
                      stackId="comparativo"
                      fill={C.success}
                      fillOpacity={0.9}
                      radius={[0, 0, 0, 0]}
                      maxBarSize={56}
                    />
                    {/* Barra: Backlog (2025 + 2026 + Novas Vendas) */}
                    <Bar
                      dataKey="backlog"
                      name="Backlog"
                      stackId="comparativo"
                      fill={C.indigo}
                      fillOpacity={0.75}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={56}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Nota sobre a leitura do gráfico */}
              <div
                style={{
                  marginTop: 12,
                  padding: "8px 14px",
                  borderRadius: 8,
                  background: isLight ? "#f8fafc" : "#0f172a",
                  border: `1px solid ${C.border}`,
                  fontSize: 11,
                  color: C.muted,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    background: C.indigo,
                    flexShrink: 0,
                  }}
                />
                As barras empilhadas representam o total de Backlog previsto
                (Backlog 2025 + Backlog 2026 + Novas Vendas 2026) somado ao que
                já foi efetivamente faturado no período. O segmento roxo indica
                o valor que ainda aguarda realização.
              </div>
            </div>
          )}
          {/* ── Tabela Fluxo de Caixa ── */}
          <div
            style={{
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: 16,
                borderBottom: `1px solid ${C.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 14, color: C.muted }}>
                DETALHAMENTO POR PERÍODO
              </span>
              {activeFilters > 0 && (
                <span style={{ fontSize: 11, color: C.muted }}>
                  Exibindo{" "}
                  <strong style={{ color: C.text }}>
                    {dadosFiltrados.length}
                  </strong>{" "}
                  de {dados.length} registros
                </span>
              )}
            </div>

            {loading ? (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: C.muted,
                  fontSize: 13,
                }}
              >
                Carregando dados…
              </div>
            ) : dadosFiltrados.length === 0 ? (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: C.muted,
                  fontSize: 13,
                }}
              >
                Nenhum registro encontrado para os filtros selecionados.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: isLight ? "#f1f5f9" : "#0f172a",
                        color: C.muted,
                      }}
                    >
                      <th style={thStyle}>Mês/Sem</th>
                      <th style={thStyle}>Saldo Inicial</th>
                      <th style={thStyle}>(+) Entradas</th>
                      <th style={thStyle}>(-) Saídas</th>
                      <th style={thStyle}>(=) Resultado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dadosFiltrados.map((row, idx) => {
                      const cf = getContasFiltradas(filterCategoria);
                      let ent = 0,
                        sai = 0;
                      Object.keys(row).forEach((k) => {
                        if (cf && !cf.has(k)) return;
                        if (k.startsWith("(+)")) ent += parseValue(row[k]);
                        if (k.startsWith("(-)")) sai += parseValue(row[k]);
                      });

                      const rowStatus = getStatusPeriodo(row["Mês"]);

                      return (
                        <tr
                          key={idx}
                          style={{ borderBottom: `1px solid ${C.border}` }}
                        >
                          <td
                            style={{
                              ...tdStyle,
                              fontWeight: 700,
                              color: C.brand,
                            }}
                          >
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              {formatPeriodo(row["Mês"], row["Semana"])}
                              <BadgeStatus status={rowStatus} />
                            </div>
                          </td>
                          <td style={tdStyle}>
                            {fmtBRL(parseValue(row["Saldo Inicial"]))}
                          </td>
                          <td style={{ ...tdStyle, color: C.success }}>
                            {fmtBRL(ent)}
                          </td>
                          <td style={{ ...tdStyle, color: C.danger }}>
                            {fmtBRL(sai)}
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              fontWeight: 800,
                              background: isLight ? "#f8fafc" : "#1e293b",
                            }}
                          >
                            {fmtBRL(parseValue(row["(=) Resultado"]))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Sub-componentes
// ─────────────────────────────────────────────

const BadgeStatus = ({ status }) => {
  if (!status) return null;
  return (
    <span
      style={{
        fontSize: "9px",
        fontWeight: 800,
        textTransform: "uppercase",
        padding: "2px 6px",
        borderRadius: "4px",
        marginLeft: "8px",
        background: `${status.color}20`,
        color: status.color,
        border: `1px solid ${status.color}40`,
        display: "inline-block",
      }}
    >
      {status.label}
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, C, color, status }) => (
  <div
    style={{
      background: C.card,
      border: `1px solid ${C.border}`,
      padding: 20,
      borderRadius: 12,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Icon size={18} color={color} />
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: C.muted,
            textTransform: "uppercase",
          }}
        >
          {label}
        </span>
      </div>
      {status && <BadgeStatus status={status} />}
    </div>
    <div style={{ fontSize: 22, fontWeight: 800 }}>{value}</div>
    {sub && (
      <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{sub}</div>
    )}
  </div>
);

const MiniBar = ({ label, valor, total, color, C }) => {
  const pct = total > 0 ? (valor / total) * 100 : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 10, color: C.muted, minWidth: 28 }}>
        {label}
      </span>
      <div
        style={{
          flex: 1,
          height: 6,
          borderRadius: 4,
          background: C.border,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: color,
            borderRadius: 4,
            transition: "width .5s",
          }}
        />
      </div>
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          color,
          minWidth: 36,
          textAlign: "right",
        }}
      >
        {pct.toFixed(0)}%
      </span>
    </div>
  );
};

// ─────────────────────────────────────────────
// Estilos
// ─────────────────────────────────────────────
const lbl = (C) => ({
  fontSize: 11,
  color: C.muted,
  display: "block",
  marginBottom: 4,
  fontWeight: 600,
});
const clrBtn = (C) => ({
  display: "flex",
  alignItems: "center",
  gap: 4,
  fontSize: 12,
  color: C.brand,
  fontWeight: 700,
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 0,
});
const btnStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 8,
  padding: "7px 12px",
  cursor: "pointer",
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
};
const thStyle = {
  textAlign: "left",
  padding: "14px 20px",
  fontWeight: 600,
  fontSize: 11,
  textTransform: "uppercase",
};
const tdStyle = { padding: "14px 20px" };

export default FluxoCaixaPage;